import React, { useContext, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  ListSubheader,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAuthContext } from "AuthContextWrapper"
import { Country, State } from "models/Geography";
import attorney1Small from "assets/attorney_1_small.png";
import attorney1 from "assets/attorney_1.png";
import attorney2Small from "assets/attorney_2_small.png";
import attorney3Small from "assets/attorney_3_small.png";
import { User } from "models/User";
import { ContactUsAPI } from "apis/ContactUs";
import { AxiosResponse } from "axios";

interface Props {
  maxTextLength?: number;
}
interface InnerProps {
  page: number;
  setPage: (pageNumber: number) => void;
  countries?: Country[];
  states?: State[];
  user?: User;
  setUser: (user?: User) => void;
  contactUsText?: string;
  setContactUsText: (text: string) => void;
  estateState?: State;
  setEstateState: (state?: State) => void;
  submittingRequest: boolean;
  setSubmittingRequest: (submitting: boolean) => void;
  responseError: string;
  setResponseError: (errorString: string) => void;
  maxTextLength: number;
}
const Pages: ((props: InnerProps) => JSX.Element)[] = [];

const ContactAnAdvisorComponent = (props?: Props): JSX.Element => {
  const [page, setPage] = React.useState<number>(1);
  const authContext = useAuthContext()
  const [user, setUser] = React.useState<User | undefined>(authContext.user);
  const [estateState, setEstateState] = React.useState<State | undefined>(
    undefined
  );
  const [contactUsText, setContactUsText] = React.useState<string>("");
  const [submittingRequest, setSubmittingRequest] =
    React.useState<boolean>(false);
  const [responseError, setResponseError] = React.useState<string>("");
  const maxTextLength = props?.maxTextLength || 400;

  useEffect(() => {
    if (authContext.states) {
      const estateState = authContext.states.find(
        (state) => state.id === authContext.activeEstate?.stateId
      );
      setEstateState(estateState);
    }
  }, [authContext.activeEstate, authContext.states]);

  return (
    <Box
      sx={{
        background: "linear-gradient(to right, #e8f4fe, #e8f4fe)",
        borderRadius: "12px",
        padding: "20px",
        boxShadow:
          "0px 1px 10px 0px rgba(116, 135, 164, 0.10), 0px 4px 5px 0px rgba(116, 135, 164, 0.11), 0px 2px 4px -1px rgba(116, 135, 164, 0.16)",
      }}
    >
      {Pages &&
        Pages[page] &&
        Pages[page]({
          page,
          setPage,
          user,
          setUser,
          contactUsText,
          setContactUsText,
          estateState,
          setEstateState,
          maxTextLength,
          countries: authContext.countries,
          states: authContext.states,
          submittingRequest,
          setSubmittingRequest,
          responseError,
          setResponseError,
        })}

      {!(Pages && Pages[page]) && (
        <Typography variant="h5" sx={{ color: "#2d2a5c", fontWeight: "bold" }}>
          Oops! Looks like we can't load the attorney-contact form at this time.
        </Typography>
      )}
    </Box>
  );
};

Pages[1] = function (props: InnerProps): JSX.Element {
  return (
    <>
      <Typography variant="h5" sx={{ color: "#2d2a5c", fontWeight: "bold" }}>
        Need some expert help? Get connected with a trusted{" "}
        {props.estateState?.name || ""} Attorney
      </Typography>
      <Grid container style={{ marginTop: "1rem" }}>
        <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
          <Typography variant="body1">
            The Atticus Approved Attorney network is here for you. Get connected
            to a professional that can provide you with the confidence,
            guidance, and support you need.
          </Typography>
          <TextField
            multiline
            label="Ask a question"
            InputLabelProps={{
              shrink: true,
            }}
            rows={5}
            style={{
              width: "100%",
              minHeight: "150px",
              borderRadius: "5px",
              color: "#333",
              marginTop: "1.5rem",
            }}
            InputProps={{
              style: { backgroundColor: "white" },
            }}
            placeholder="I&rsquo;m curious about..."
            value={props.contactUsText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              if (e.target.value.length <= props.maxTextLength) {
                props.setContactUsText(e.target.value);
              }
            }}
          />
          <Typography
            component="p"
            variant="body2"
            style={{ width: "100%", textAlign: "right", color: "#aaa" }}
          >
            {props.maxTextLength - (props.contactUsText?.length || 0)}{" "}
            characters remaining
          </Typography>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              props.setPage(props.page + 1);
            }}
          >
            Next
          </Button>
        </Grid>
        <Grid item xs={0} sm={0} md={5} lg={5} xl={5}>
          <div style={{ clear: "both", overflow: "hidden", width: "100%" }}>
            <Box
              display={{
                xs: "none",
                sm: "none",
                md: "block",
                lg: "none",
                xl: "none",
              }}
              style={{ width: "100%" }}
            >
              <img
                src={attorney2Small}
                style={{
                  width: "170px",
                  float: "left",
                  position: "absolute",
                  zIndex: 3,
                  marginLeft: "calc(15% - 100px)",
                  marginTop: "40px",
                }}
              />
              <img
                src={attorney1Small}
                style={{
                  width: "200px",
                  float: "right",
                  position: "absolute",
                  zIndex: 5,
                  marginLeft: "15%",
                  marginTop: "40px",
                }}
              />
            </Box>
            <Box
              display={{
                xs: "none",
                sm: "none",
                md: "none",
                lg: "block",
                xl: "none",
              }}
            >
              <img
                src={attorney2Small}
                style={{
                  width: "130px",
                  float: "left",
                  position: "absolute",
                  zIndex: 3,
                  marginLeft: "calc(12% - 80px)",
                  marginTop: "60px",
                }}
              />
              <img
                src={attorney1Small}
                style={{
                  width: "230px",
                  float: "left",
                  position: "absolute",
                  zIndex: 5,
                  marginLeft: "12%",
                }}
              />
              <img
                src={attorney3Small}
                style={{
                  width: "130px",
                  float: "left",
                  position: "absolute",
                  zIndex: 2,
                  marginLeft: "calc(12% + 140px)",
                  marginTop: "60px",
                }}
              />
            </Box>
            <Box
              display={{
                xs: "none",
                sm: "none",
                md: "none",
                lg: "none",
                xl: "block",
              }}
            >
              <img
                src={attorney2Small}
                style={{
                  width: "160px",
                  float: "left",
                  position: "absolute",
                  zIndex: 3,
                  marginLeft: "calc(12% - 105px)",
                  marginTop: "60px",
                }}
              />
              <img
                src={attorney1Small}
                style={{
                  width: "260px",
                  float: "left",
                  position: "absolute",
                  zIndex: 5,
                  marginLeft: "12%",
                }}
              />
              <img
                src={attorney3Small}
                style={{
                  width: "160px",
                  float: "left",
                  position: "absolute",
                  zIndex: 2,
                  marginLeft: "calc(12% + 165px)",
                  marginTop: "60px",
                }}
              />
            </Box>
          </div>
        </Grid>
      </Grid>
    </>
  );
};

Pages[2] = function (props: InnerProps): JSX.Element {
  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
          <Typography
            component="h5"
            variant="h4"
            sx={{ color: "#2d2a5c", fontWeight: "bold" }}
          >
            Great! Now, how can we stay in touch?
          </Typography>
          <Typography variant="body2" style={{ marginTop: "1rem" }}>
            We need a few quick pieces of information so we can get you
            connected with the right professional
          </Typography>

          <TextField
            required={true}
            value={props.user?.fullName}
            label="Your name"
            style={{
              width: "100%",
              borderRadius: "5px",
              color: "#333",
              marginTop: "1rem",
            }}
            InputProps={{
              style: { backgroundColor: "white" },
            }}
            onChange={(e) =>
              props.setUser({ ...props.user, fullName: e.target.value } as User)
            }
          />

          <Grid container style={{ marginTop: "1rem" }}>
            <Grid
              item
              style={{ paddingRight: "1rem" }}
              xs={6}
              sm={6}
              lg={6}
              xl={6}
            >
              <TextField
                required={true}
                value={props.user?.email}
                label="Email address"
                style={{ width: "100%", borderRadius: "5px", color: "#333" }}
                InputProps={{
                  style: { backgroundColor: "white" },
                }}
                onChange={(e) =>
                  props.setUser({
                    ...props.user,
                    email: e.target.value,
                  } as User)
                }
              />
            </Grid>
            <Grid item xs={6} sm={6} lg={6} xl={6}>
              <TextField
                value={props.user?.phone}
                label="Phone number"
                style={{ width: "100%", borderRadius: "5px", color: "#333" }}
                InputProps={{
                  style: { backgroundColor: "white" },
                }}
                onChange={(e) =>
                  props.setUser({
                    ...props.user,
                    phone: e.target.value,
                  } as User)
                }
              />
            </Grid>
          </Grid>

          <FormControl
            style={{
              marginTop: "1rem",
              width: "100%",
            }}
          >
            <InputLabel>Estate Location</InputLabel>
            <Select
              style={{ backgroundColor: "white" }}
              value={props.estateState?.slug}
              label="Estate Location"
              onChange={(event: SelectChangeEvent<string>) => {
                event.preventDefault();
                event.stopPropagation();
                props.setEstateState(
                  props.states?.find(
                    (state) => state.slug === event.target.value
                  )
                );
              }}
            >
              {getEstateLocationOptions(
                props.countries || [],
                props.states || []
              )}
            </Select>
          </FormControl>
          <LoadingButton
            type="button"
            color="primary"
            variant="contained"
            style={{ marginTop: "1rem" }}
            loading={props.submittingRequest}
            onClick={() => {
              props.setSubmittingRequest(true);
              const api = new ContactUsAPI();
              api
                .sendFeedback(
                  [
                    "Contact an advisor form submitted",
                    "From " +
                      (props.user?.first || "") +
                      " " +
                      (props.user?.last || "") +
                      (props.user?.email ? ` <${props.user.email}>` : ""),
                    "\n",
                    props.contactUsText,
                  ].join("\n")
                )
                .then((res: AxiosResponse | null) => {
                  if (res?.status !== 200) {
                    props.setSubmittingRequest(false);
                    props.setResponseError(
                      "An error occurred while submitting your request.  Please email us at support@weareatticus.biz!"
                    );
                    return;
                  }

                  props.setPage(props.page + 1);
                });
            }}
          >
            Submit your question
          </LoadingButton>
        </Grid>
        <Grid item xs={0} sm={0} md={4} lg={4} xl={4}>
          <Box
            display={{
              xs: "none",
              sm: "none",
              md: "none",
              lg: "block",
              xl: "block",
            }}
          >
            <img
              src={attorney1}
              style={{
                width: "90%",
                marginLeft: "12%",
                marginTop: "12%",
                maxWidth: "300px",
              }}
            />
          </Box>
          <Box
            display={{
              xs: "none",
              sm: "none",
              md: "block",
              lg: "none",
              xl: "none",
            }}
          >
            <img
              src={attorney1}
              style={{ width: "90%", marginLeft: "8%", marginTop: "100px" }}
            />
          </Box>
        </Grid>
      </Grid>
      {props.responseError && (
        <Typography variant={"body2"} sx={{ color: "red" }}>
          {props.responseError}
        </Typography>
      )}
    </>
  );
};

Pages[3] = function (): JSX.Element {
  return (
    <div style={{ textAlign: "center", paddingBottom: "1rem" }}>
      <img
        src={"/static/icons/Atticus-heart.svg"}
        style={{
          width: "200px",
          marginTop: "1rem",
          marginLeft: "calc(50%-100px)",
        }}
      />
      <Typography
        component="h5"
        variant="h4"
        sx={{ color: "#2d2a5c", fontWeight: "bold", marginTop: "1rem" }}
      >
        Success! We’ll reach out to you when we find a good match
      </Typography>
    </div>
  );
};

function getEstateLocationOptions(countries: Country[], states: State[]) {
  const items = [];

  for (const country of countries) {
    items.push(
      <ListSubheader
        key={country.code}
        sx={{
          borderTop: "1px solid #e3e3e3",
          borderBottom: "1px solid #e3e3e3",
          backgroundColor: "#F2F2FF",
          fontWeight: "bold",
        }}
      >
        {country.name}
      </ListSubheader>
    );
    for (const state of states) {
      if (state.countryId === country.id) {
        items.push(
          <MenuItem key={state.slug} value={state.slug}>
            {state.name}
          </MenuItem>
        );
      }
    }
  }
  return items;
}

export default ContactAnAdvisorComponent;
